/**
 * University Show Page - Modular Alpine.js Implementation
 * Main component that combines all modules
 */

import { commentSystem } from './components/comment-system.js';
import { reviewInteractions } from './components/review-interactions.js';
import { feedbackSystem } from './components/feedback-system.js';
// Removed modal system import - now using wire-elements/modal

// Define the component factory function
const universityShowComponent = (config) => ({
    // Configuration
    config: config || {},
    routes: config?.routes || {},
    assets: config?.assets || {},
    authStatus: config?.authStatus || {},
    universityId: config?.universityId || null,

    // Import all module states and methods
    ...commentSystem,
    ...reviewInteractions,
    ...feedbackSystem,

    // Main initialization
    init() {
        this.initializeComponents();
        this.setupEventListeners();
        this.initializeAnimations();
    },

    // Initialize all components
    initializeComponents() {
        // Comment system is now handled by Livewire component
        // this.initializeCommentSystem();

        // Initialize review interactions
        this.initializeReviewInteractions();

        // Feedback system is now handled by Livewire component
        // this.initializeFeedbackSystem();
    },

    // Setup global event listeners
    setupEventListeners() {
        // Listen for auth events
        window.addEventListener('auth-success', () => {
            window.location.reload();
        });

        // Listen for review modal events (deprecated - use direct component calls instead)
        window.addEventListener('open-review-modal', () => {
            // This is deprecated - modals should be opened directly with component name and arguments
            console.warn('open-review-modal event is deprecated. Use direct modal dispatch instead.');
        });

        // Listen for universal modal events
        window.addEventListener('open-modal', (event) => {
            const modalId = event.detail?.id;
            if (modalId) {
                // Handle Alpine.js modals (university-review-modal)
                if (modalId === 'university-review-modal') {
                    const modal = document.getElementById('university-review-modal');
                    if (modal) {
                        modal.dispatchEvent(new CustomEvent('open-modal'));
                    }
                    return;
                }

                // Convert old modal IDs to new component names
                const componentMap = {
                    'university-sharing-modal': 'universities.sharing-modal',
                    'profile-report-modal': 'professors.report-modal'
                };

                const componentName = componentMap[modalId] || modalId;

                // Get university ID for modals that need it
                const universityId = (() => {
                    const component = document.querySelector('[x-data*="universityShow"]')?.__x?.$data;
                    return component?.universityId || window.universityId || null;
                })();

                // Use Livewire for remaining modals
                window.Livewire.dispatch('openModal', { component: componentName });
            }
        });
    },

    // Initialize animations
    initializeAnimations() {
        // Add animation delays to review cards
        this.$nextTick(() => {
            const reviewCards = document.querySelectorAll('.review-card');
            reviewCards.forEach((card, index) => {
                card.style.animationDelay = `${index * 0.1}s`;
                card.classList.add('animate-fade-in');
            });
        });

    },


    // Utility Functions
    getCsrfToken() {
        return this.authStatus.csrfToken ||
            document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') || '';
    },

    // Show message helper
    showMessage(type, message) {
        const errorDiv = document.getElementById('comment-error');
        const successDiv = document.getElementById('comment-success');

        // The comment error/success containers only exist on pages that
        // render the legacy comment form — fall back to the global toast so
        // messages are never silently dropped (issue #7).
        if (!errorDiv && !successDiv) {
            if (typeof window.showNotification === 'function') {
                window.showNotification(message, type === 'error' ? 'error' : 'success');
            } else {
                console.warn('[universityShow]', type, message);
            }
            return;
        }

        if (type === 'error') {
            if (errorDiv) {
                errorDiv.querySelector('p').textContent = message;
                errorDiv.classList.remove('hidden');
            }
            if (successDiv) {
                successDiv.classList.add('hidden');
            }
        } else {
            if (successDiv) {
                successDiv.querySelector('p').textContent = message;
                successDiv.classList.add('hidden');
            }
            if (errorDiv) {
                errorDiv.classList.add('hidden');
            }
        }
    }
});

// Register the component with Alpine
const registerUniversityShowComponent = () => {
    if (typeof Alpine !== 'undefined' && Alpine.data) {
        Alpine.data('universityShow', universityShowComponent);
        return true;
    }
    return false;
};

// Register immediately if Alpine is available
if (!registerUniversityShowComponent()) {
    // If Alpine is not ready yet, wait for it
    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', () => {
            // Try again after DOM is loaded
            if (!registerUniversityShowComponent()) {
                document.addEventListener('alpine:init', () => {
                    Alpine.data('universityShow', universityShowComponent);
                }, { once: true });
            }
        });
    } else {
        // DOM is already loaded, just wait for Alpine
        document.addEventListener('alpine:init', () => {
            Alpine.data('universityShow', universityShowComponent);
        }, { once: true });
    }
}

// Global functions for backward compatibility
window.openReviewModal = (universityId) => {
    if (!universityId) {
        console.error('University ID is required to open review modal');
        return;
    }
    // Open Alpine.js modal instead of Livewire
    const modal = document.getElementById('university-review-modal');
    if (modal) {
        modal.dispatchEvent(new CustomEvent('open-modal'));
    } else {
        console.warn('University review modal not found');
    }
};

window.openSharingModal = () => {
    window.Livewire.dispatch('openModal', { component: 'features.universities.university-sharing-modal' });
};

window.openReportModal = (reportableId, reportableType = 'universities') => {
    window.Livewire.dispatch('openModal', {
        component: 'professors.report-modal',
        arguments: { reportableId, reportableType }
    });
};

